import Link from 'next/link';

import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import { LAST_CHECKED, OurMark, SourceRefs, usd } from './kit';
import type { AlternativePageProps, OurPlan, ResolvedPlan } from './types';
import {
  ArrowGlyph,
  buttonPrimary,
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '../primitives';
import { useReveal } from '../useReveal';

const plain = (value: number) => new Intl.NumberFormat('en-US').format(value);

const compact = (value: number) =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

/** Their plans as listed, beside ours as sold today. */
export default function PricingCompare({
  name,
  pricing,
  ours,
}: Pick<AlternativePageProps, 'name' | 'pricing' | 'ours'>) {
  const { ref, state } = useReveal<HTMLDivElement>(0.15);

  return (
    <section id='pricing' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='What you’d pay.'
          tail={`US dollars before tax, checked ${LAST_CHECKED}.`}
        />

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(
            styles.reveal,
            'mt-12 grid items-start gap-5 lg:grid-cols-2',
          )}
        >
          <div
            className={clsxm(
              styles.rv,
              'rounded-[28px] p-6 sm:p-8',
              tone.surface,
              tone.line,
            )}
            style={delay(0)}
          >
            <p className={clsxm('text-lg font-semibold', tone.ink)}>{name}</p>
            <p className={clsxm('mt-1 text-sm', tone.quiet)}>
              As listed on {name}’s own pages
            </p>
            <ul className='mt-5 divide-y divide-[#0f0f28]/[0.07] border-y border-[#0f0f28]/[0.07] dark:divide-white/[0.08] dark:border-white/[0.08]'>
              {pricing.plans.map((plan) => (
                <TheirPlan key={plan.name} plan={plan} />
              ))}
            </ul>
            <p className={clsxm('mt-6 text-[15px] leading-7', tone.body)}>
              {pricing.note}
              <SourceRefs ids={pricing.sources} />
            </p>
          </div>

          <div
            className={clsxm(
              styles.rv,
              'rounded-[28px] p-6 ring-2 ring-primary-500/60 dark:ring-primary-400/50 sm:p-8',
              tone.surface,
            )}
            style={delay(120)}
          >
            <p className='flex items-center gap-3'>
              <OurMark />
              <span>
                <span
                  className={clsxm('block text-lg font-semibold', tone.ink)}
                >
                  Easy-Peasy.AI
                </span>
                <span className={clsxm('block text-sm', tone.quiet)}>
                  Today’s prices from our checkout
                </span>
              </span>
            </p>
            <ul className='mt-5 divide-y divide-[#0f0f28]/[0.07] border-y border-[#0f0f28]/[0.07] dark:divide-white/[0.08] dark:border-white/[0.08]'>
              {ours.plans.map((plan) => (
                <OurPlanRow key={plan.name} plan={plan} />
              ))}
            </ul>
            <p className={clsxm('mt-6 text-[15px] leading-7', tone.body)}>
              Paid plans have no message cap. Replies from premium models use
              your plan’s monthly words; {ours.unmetered.length} models, such as{' '}
              {ours.unmetered.slice(0, 2).join(' and ')}, use none. Phone
              minutes are billed by Twilio.
            </p>
            <div className='mt-7 flex flex-wrap items-center gap-x-5 gap-y-3'>
              <Link href='/login?preset=bots' className={buttonPrimary}>
                Build your agent free
                <ArrowGlyph />
              </Link>
              <Link
                href='/chatbots#pricing'
                className='text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
              >
                All agent plans
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function priceLines(plan: ResolvedPlan): { main: string; sub?: string } {
  if (plan.priceLabel) return { main: plan.priceLabel };
  const from = plan.from ? 'from ' : '';
  if (plan.monthly === 0) return { main: '$0' };
  if (plan.monthly != null) {
    return {
      main: `${from}${usd(plan.monthly)}/mo`,
      sub:
        plan.yearly != null
          ? `${from}${usd(plan.yearly)}/mo billed yearly`
          : undefined,
    };
  }
  if (plan.yearly != null) {
    return { main: `${from}${usd(plan.yearly)}/mo`, sub: 'billed yearly' };
  }
  return { main: 'Not published' };
}

function TheirPlan({ plan }: { plan: ResolvedPlan }) {
  const price = priceLines(plan);
  return (
    <li className='grid gap-x-6 gap-y-2 py-5 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]'>
      <div>
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          {plan.name}
          <SourceRefs ids={plan.sources} />
        </p>
        <p
          className={clsxm(
            'mt-1 text-xl font-semibold tracking-tight',
            tone.ink,
          )}
        >
          {price.main}
          {plan.unit && (
            <span className={clsxm('ms-1 text-sm font-normal', tone.quiet)}>
              {plan.unit}
            </span>
          )}
        </p>
        {price.sub && (
          <p className={clsxm('text-xs', tone.quiet)}>{price.sub}</p>
        )}
      </div>
      <ul className='space-y-1.5 sm:pt-0.5'>
        {plan.includes.map((line) => (
          <li
            key={line}
            className={clsxm('flex gap-2 text-sm leading-6', tone.body)}
          >
            <span
              aria-hidden='true'
              className='mt-[9px] h-1 w-1 flex-none rounded-full bg-[#8a8aa1]'
            />
            {line}
          </li>
        ))}
      </ul>
    </li>
  );
}

function OurPlanRow({ plan }: { plan: OurPlan }) {
  const isFree = plan.name === 'Free';
  // "Everything in Starter" reads first, then the plan's own numbers.
  const inherited = plan.includes.filter((line) =>
    line.startsWith('Everything'),
  );
  const extras = plan.includes.filter((line) => !line.startsWith('Everything'));
  const lines = [
    ...inherited,
    `${plan.agents} ${plan.agents === 1 ? 'agent' : 'agents'}`,
    ...(isFree ? [] : [`${plan.actions} actions per agent`]),
    `${compact(plan.characters)} characters of knowledge per agent`,
    ...(plan.words
      ? [`${plain(plan.words)} words a month on premium models`]
      : []),
    ...extras,
  ];

  let main = 'See pricing';
  let sub: string | undefined;
  if (isFree) {
    main = '$0';
    sub = 'No credit card';
  } else if (plan.monthly) {
    main = `${usd(plan.monthly)}/mo`;
    sub = plan.yearly ? `${usd(plan.yearly)}/mo billed yearly` : undefined;
  }

  return (
    <li className='grid gap-x-6 gap-y-2 py-5 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]'>
      <div>
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          {plan.name}
        </p>
        <p
          className={clsxm(
            'mt-1 text-xl font-semibold tracking-tight',
            tone.ink,
          )}
        >
          {main}
        </p>
        {sub && <p className={clsxm('text-xs', tone.quiet)}>{sub}</p>}
      </div>
      <ul className='space-y-1.5 sm:pt-0.5'>
        {lines.map((line) => (
          <li
            key={line}
            className={clsxm('flex gap-2 text-sm leading-6', tone.body)}
          >
            <CheckGlyph className='mt-1 h-3.5 w-3.5 text-primary-600 dark:text-primary-400' />
            {line}
          </li>
        ))}
      </ul>
    </li>
  );
}
